import { useEffect, useRef } from 'react';
import { queryClient } from 'providers/QueryProvider';
import {
  useSelectedOrganizationId,
  useTenantScopeRevision,
} from 'stores/useAuthStore';
import { useIsOrgMismatch } from 'containers/Organizations/hooks/useIsOrgMismatch';
import { clearDashboardSelection } from 'stores/useDashboardStore';
import { useBaseViewUiStore } from 'stores/useBaseViewUiStore';
import {
  initialLocatingFilterState,
  useLocatingFilterStore,
} from 'stores/useLocatingFilterStore';
import { useCallStore } from 'stores/useCallStore';
import { useAgentStore } from 'stores/useAgentStore';
import { useTagDataStore } from 'stores/useTagDataStore';

const PRESERVED_QUERY_ROOTS = new Set([
  'currentUser',
  'entities',
  'organizations',
]);

export function resetTenantScopedState(): void {
  queryClient.resetQueries({
    predicate: (query) => {
      const queryRoot = query.queryKey[0];
      return (
        typeof queryRoot !== 'string' ||
        !PRESERVED_QUERY_ROOTS.has(queryRoot)
      );
    },
  });

  resetTenantScopedStores();
}

export function resetTenantScopedStores(): void {
  clearDashboardSelection();
  useBaseViewUiStore.getState().reset();
  useLocatingFilterStore.setState({ filterState: initialLocatingFilterState });
  useCallStore.getState().reset();
  useTagDataStore.getState().reset();
  useAgentStore.setState({
    reportAnalysis: null,
    finalAnswer: null,
    currentNodeName: null,
  });
}

export function useTenantScopeReset(): void {
  const [isOrgMismatch, resolveMismatch] = useIsOrgMismatch();
  const organizationId = useSelectedOrganizationId();
  const tenantScopeRevision = useTenantScopeRevision();
  const establishedOrgRef = useRef(organizationId);
  const tenantScopeRevisionRef = useRef(tenantScopeRevision);

  useEffect(() => {
    if (!isOrgMismatch) return;
    const wasResetBeforeCommit =
      tenantScopeRevisionRef.current !== tenantScopeRevision;
    if (Number.isFinite(establishedOrgRef.current) && !wasResetBeforeCommit) {
      resetTenantScopedState();
    }
    establishedOrgRef.current = organizationId;
    tenantScopeRevisionRef.current = tenantScopeRevision;
    resolveMismatch();
  }, [
    isOrgMismatch,
    organizationId,
    resolveMismatch,
    tenantScopeRevision,
  ]);
}
